



import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import Modal from '@kasasa/kloans-common-ui/dist/components/Modal/Modal.vue';
import TermsOfUse from '@/components/TermsOfUse/TermsOfUse.vue';
import {
	CodeValidation,
	DateValidation,
	emailValidation,
	requiredOnlyValidation,
} from '@kasasa/kloans-common-ui/dist/validations';
import {
	postConsumerStatus,
	postEnrollmentResume,
	postKcasEnrollmentVerifyTokenData,
	postMortgageLinkEmail,
} from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication';
import {
	addProfileConnection, setJwt,
} from '@kasasa/kloans-common-ui/dist/consumer-app/services/kcas-profile';
import { AxiosPromise, AxiosResponse } from 'axios';
import { PostConsumerStatusResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/ConsumerStatus';
import { yyyymmdd } from '@kasasa/kloans-common-ui/dist/utils/format';
import { PostEnrollmentResumeResponse } from '@kasasa/kloans-common-ui/dist/consumer-app/services/kloans-authentication/types/Enrollment';
import { ProductInviteType } from '@/enums/ProductInviteType';
import { sessionService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/session';
import { Auth } from '@kasasa/kloans-common-ui/dist/consumer-app/services/session/types';
import { amplifyService } from '@kasasa/kloans-common-ui/dist/consumer-app/services/amplify';

@Component({
	components: {
		Modal,
		TermsOfUse,
	},
	validations: {
		last4Ssn: CodeValidation.validations(4),
		dateOfBirth: DateValidation.validations('MM/DD/YYYY'),
		accountNumber: requiredOnlyValidation.validations,
		email: emailValidation.validations,
	},
})
export default class ProductInvite extends Vue {
	@Prop({ default: '' })
	token!: string;

	@Prop({ default: ProductInviteType.NEW_PROFILE_CREATION })
	inviteType!: ProductInviteType | null;

	showTerms: boolean = false;

	last4Ssn: string = '';

	dateOfBirth: string = '';

	termsAgreement: boolean = false;

	formErrorMessage: string = '';

	loading: boolean = false;

	isMortgageDirectAccessWithNoEmail: boolean = false;

	showEmailMessage: boolean = false;

	accountNumber: string = '';

	email: string = '';

	loanType: string = '';

	showProfileConnectionIssuesModal: boolean = false;

	async created(): Promise<void> {
		this.$root.$on('closeTermsModal', this.closeTermsModal);
		if (this.$route.name === 'kasasaMortgageInvite') {
			this.isMortgageDirectAccessWithNoEmail = true;
			this.loanType = 'Mortgage';
		} else {
			this.loanType = 'Loan';
		}

		// When the flow is profile connection, we need to authenticate the user first
		if (this.isProfileConnectionInvite && !sessionService.isAuthenticated()) {
			await this.profileConnectionLoginRedirect();
			return;
		}

		// If not a profile connection invite, redirect authenticated users to the dashboard
		if (!this.isProfileConnectionInvite && sessionService.isAuthenticated()) {
			this.redirectToDashboard();
		}
	}

	// eslint-disable-next-line class-methods-use-this
	redirectToDashboard(): void {
		// Once our apps are merged together, we will be able to use vue router for this
		window.location.href = '/profile/#/accounts';
	}

	get isProfileConnectionInvite(): boolean {
		return this.inviteType === ProductInviteType.CONNECT_EXISTING_PROFILE;
	}

	get headerText(): string {
		if (this.isMortgageDirectAccessWithNoEmail) {
			return 'Welcome to your mortgage!';
		}

		return this.isProfileConnectionInvite
			? 'Welcome to your Kasasa Loan!'
			: 'Welcome to your Loan Dashboard!';
	}

	get bodyText(): string {
		if (this.isMortgageDirectAccessWithNoEmail) {
			return 'Verify your info on the right to get started with your mortgage.';
		}

		return this.isProfileConnectionInvite
			? 'Verify your info on the right to get started with your Kasasa Loan, the most flexible loan.'
			: 'Verify your info on the right to set up your dashboard.';
	}

	get last4Errors() {
		return CodeValidation.errors(this, 'last4Ssn', '4', 'Last 4 SSN');
	}

	get dateOfBirthErrors() {
		return DateValidation.errors(this, 'dateOfBirth', 'MM/DD/YYYY');
	}

	get accountNumberErrors() {
		return requiredOnlyValidation.errors(this, 'accountNumber');
	}

	get emailErrors() {
		return emailValidation.errors(this);
	}

	closeTermsModal() {
		this.showTerms = false;
	}

	showTermsModal() {
		this.showTerms = true;
	}

	submit(): void {
		this.$v.$touch();
		if (this.isMortgageDirectAccessWithNoEmail) {
			this.submitMortgage();
		} else {
			this.submitInMarket();
		}
	}

	async submitInMarket() {
		if (!this.$v.last4Ssn.$invalid && !this.$v.dateOfBirth.$invalid) {
			this.formErrorMessage = '';

			if (!this.termsAgreement) {
				this.formErrorMessage = 'You must agree to the terms of use before continuing.';
			} else {
				this.loading = true;

				try {
					const response = await postKcasEnrollmentVerifyTokenData(this.token, {
						dateOfBirth: yyyymmdd(this.dateOfBirth),
						ssnLast4: this.last4Ssn,
					});

					sessionStorage.setItem('token', this.token);
					sessionStorage.setItem('last4Ssn', this.last4Ssn);
					sessionStorage.setItem('dateOfBirth', this.dateOfBirth);

					// Call the profile service to add the connection
					if (this.isProfileConnectionInvite) {
						// Due to our current rest service setup, we need to pass the jwt here
						const jwt: string = (localStorage.getItem(Auth.ID_TOKEN) as string);
						setJwt(jwt);

						await addProfileConnection(response.data.verificationNonce);

						// Get a new token with the kloans connection data
						const newJwt: string = await this.refreshJwtToken();
						localStorage.setItem(Auth.ID_TOKEN, newJwt);
						// Add to session storage for backwards compatibility
						sessionStorage.setItem(Auth.ID_TOKEN, newJwt);

						this.redirectToDashboard();
						return;
					}

					await this.checkEnrollmentStep();
				} catch (error) {
					if (error.response?.status === 403 && error.response?.data?.verifyTries === 0) {
						this.formErrorMessage = 'Maximum invalid attempts reached.';

						setTimeout(() => this.$router.push({ name: 'inviteTokenExpiredMessage' }), 3000);
						return;
					}

					// For profile connection issues, UX mocks were designed to use a modal when displaying errors
					if (this.isProfileConnectionInvite) {
						this.showProfileConnectionIssuesModal = true;
					} else {
						this.formErrorMessage = `This information does not match the information we have on file
						for you. If you continue to see this error, please contact your Kasasa institution for
						assistance.`;
					}
				} finally {
					this.loading = false;
				}
			}
		}
	}

	get btnText(): string {
		if (this.loading) {
			return 'Loading...';
		}

		return this.isProfileConnectionInvite ? 'Connect Account' : 'Get Started';
	}

	async submitMortgage() {
		if (!this.$v.$invalid) {
			this.formErrorMessage = '';
			if (!this.termsAgreement) {
				this.formErrorMessage = 'You must agree to the terms of use before continuing.';
			} else {
				try {
					this.loading = true;

					await postMortgageLinkEmail({
						mortgageNumber: this.accountNumber,
						last4Ssn: this.last4Ssn,
						dob: yyyymmdd(this.dateOfBirth),
						email: this.email,
					});
					this.showEmailMessage = true;
				} catch (error) {
					const { status } = error.response;
					let message: string;

					switch (status) {
						case 404:
							message = 'The information provided is not correct. Please try again.';
							break;
						case 409:
							message = 'This email is already in use. Please enter a different email.';
							break;
						case 410:
							message = 'A profile already exists for this borrower. Please log into your account <a href="/login/">here.</a>';
							break;
						default:
							message = 'An unknown error has occurred.';
							break;
					}

					this.formErrorMessage = message;
				} finally {
					this.loading = false;
				}
			}
		}
	}

	async checkEnrollmentStep(): Promise<void> {
		let route: string | null;
		let response: AxiosResponse<PostConsumerStatusResponse>;
		try {
			this.loading = true;

			response = await postConsumerStatus({
				token: this.token,
				// eslint-disable-next-line @typescript-eslint/camelcase
				date_of_birth: this.dateOfBirth,
				// eslint-disable-next-line @typescript-eslint/camelcase
				ssn_last_4: this.last4Ssn,
			});
		} catch (error) {
			this.formErrorMessage = 'There was an error continuing your enrollment. Please contact your Kasasa institution.';
			return;
		} finally {
			this.loading = false;
		}

		const { status } = response.data.data;
		switch (status) {
			case 'unregistered':
				route = 'createLogin';
				break;
			case 'registered':
				route = 'verifyMobileDevice';
				break;
			case 'confirmed':
				route = 'pinSetup';
				break;
			case 'active':
				route = 'login';
				break;
			default:
				route = null;
				break;
		}

		if (route) {
			this.goToCorrectEnrollmentStep(route);
		}
	}

	goToCorrectEnrollmentStep(routeName: string) {
		if (routeName === 'createLogin') {
			this.$router.push({ name: routeName });
			const timeout: Date = new Date();
			timeout.setMinutes(timeout.getMinutes() + 10);
			sessionStorage.setItem('enrollmentTouch', timeout.valueOf().toString());
		} else {
			this.resumeEnrollment(routeName);
		}
	}

	async resumeEnrollment(route: string) {
		this.loading = true;

		try {
			const response: AxiosResponse<PostEnrollmentResumeResponse> = await (postEnrollmentResume({
				token: this.token,
				// eslint-disable-next-line @typescript-eslint/camelcase
				ssn_last_4: this.last4Ssn,
				// eslint-disable-next-line @typescript-eslint/camelcase
				date_of_birth: this.dateOfBirth,
			}) as AxiosPromise<PostEnrollmentResumeResponse>);
			const kjwt: string = response.data.data;
			this.setKjwt(kjwt, route);
		} catch (error) {
			this.formErrorMessage = 'There was an error continuing your enrollment. Please contact your Kasasa institution.';
		} finally {
			this.loading = false;
		}
	}

	setKjwt(kjwt: string, routeName: string) {
		sessionStorage.setItem('kjwt', kjwt);
		// Timeout in 10 minutes.
		const timeout: Date = new Date();
		timeout.setMinutes(timeout.getMinutes() + 10);
		sessionStorage.setItem('enrollmentTouch', timeout.valueOf().toString());
		this.$router.push({ name: routeName });
	}

	async profileConnectionLoginRedirect(): Promise<void> {
		await this.$router.push({
			name: 'login',
			query: {
				token: this.token,
			},
		});
	}

	// eslint-disable-next-line class-methods-use-this
	async refreshJwtToken(): Promise<string> {
		return new Promise((resolve) => {
			amplifyService.refreshJwtToken((token: string) => {
				resolve(token);
			});
		});
	}

	resubmitInformation(): void {
		this.showEmailMessage = false;
	}
}
